import { symbol as symbol$1 } from '../../Equal/dist/effect-Equal.esm.js';
import { dual } from '../../Function/dist/effect-Function.esm.js';
import { symbol, structure } from '../../Hash/dist/effect-Hash.esm.js';
import { toString, NodeInspectSymbol } from '../../Inspectable/dist/effect-Inspectable.esm.js';
import { some, none } from '../../Option/dist/effect-Option.esm.js';
import { make as make$1, between as between$1, mapInput, min as min$1, max as max$1, clamp as clamp$1 } from '../../Order/dist/effect-Order.esm.js';
import { pipeArguments } from '../../Pipeable/dist/effect-Pipeable.esm.js';
import { isNumber, isBigInt } from '../../Predicate/dist/effect-Predicate.esm.js';

/**
 * @since 2.0.0
 */
const TypeId = /*#__PURE__*/Symbol.for("effect/Duration");
const bigint1e3 = /*#__PURE__*/BigInt(1_000);
const bigint1e9 = /*#__PURE__*/BigInt(1_000_000_000);

/**
 * @since 2.0.0
 * @category symbol
 */

/**
 * @since 2.0.0
 * @category models
 */

/**
 * @since 2.0.0
 * @category models
 */

/**
 * @since 2.0.0
 * @category models
 */

/**
 * @since 2.0.0
 * @category models
 */

const DURATION_REGEX = /^(-?\d+(?:\.\d+)?)\s+(nanos|micros|millis|seconds|minutes|hours|days|weeks)$/;

/**
 * @since 2.0.0
 */
const decode = input => {
  if (isDuration(input)) {
    return input;
  } else if (isNumber(input)) {
    return millis(input);
  } else if (isBigInt(input)) {
    return nanos(input);
  } else {
    DURATION_REGEX.lastIndex = 0; // Reset the lastIndex before each use
    const match = DURATION_REGEX.exec(input);
    if (match) {
      const [_, valueStr, unit] = match;
      const value = Number(valueStr);
      switch (unit) {
        case "nanos":
          return nanos(BigInt(valueStr));
        case "micros":
          return micros(BigInt(valueStr));
        case "millis":
          return millis(value);
        case "seconds":
          return seconds(value);
        case "minutes":
          return minutes(value);
        case "hours":
          return hours(value);
        case "days":
          return days(value);
        case "weeks":
          return weeks(value);
      }
    }
  }
  throw new Error("Invalid duration input");
};
const zeroValue = {
  _tag: "Millis",
  millis: 0
};
const infinityValue = {
  _tag: "Infinity"
};
const DurationProto = {
  [TypeId]: TypeId,
  [symbol]() {
    return structure(this.value);
  },
  [symbol$1](that) {
    return isDuration(that) && equals(this, that);
  },
  toString() {
    return toString(this.toJSON());
  },
  toJSON() {
    switch (this.value._tag) {
      case "Millis":
        return {
          _id: "Duration",
          _tag: "Millis",
          millis: this.value.millis
        };
      case "Nanos":
        return {
          _id: "Duration",
          _tag: "Nanos",
          hrtime: toHrTime(this)
        };
      case "Infinity":
        return {
          _id: "Duration",
          _tag: "Infinity"
        };
    }
  },
  [NodeInspectSymbol]() {
    return this.toJSON();
  },
  pipe() {
    return pipeArguments(this, arguments);
  }
};
const make = input => {
  const duration = Object.create(DurationProto);
  if (isNumber(input)) {
    if (isNaN(input) || input < 0) {
      duration.value = zeroValue;
    } else if (!Number.isFinite(input)) {
      duration.value = infinityValue;
    } else if (!Number.isInteger(input)) {
      duration.value = {
        _tag: "Nanos",
        nanos: BigInt(Math.round(input * 1_000_000))
      };
    } else {
      duration.value = {
        _tag: "Millis",
        millis: input
      };
    }
  } else if (input < BigInt(0)) {
    duration.value = zeroValue;
  } else {
    duration.value = {
      _tag: "Nanos",
      nanos: input
    };
  }
  return duration;
};

/**
 * @since 2.0.0
 * @category guards
 */
const isDuration = u => typeof u === "object" && u !== null && TypeId in u;

/**
 * @since 2.0.0
 * @category constructors
 */
const zero = /*#__PURE__*/make(0);

/**
 * @since 2.0.0
 * @category constructors
 */
const infinity = /*#__PURE__*/make(Infinity);

/**
 * @since 2.0.0
 * @category constructors
 */
const nanos = nanos => make(nanos);

/**
 * @since 2.0.0
 * @category constructors
 */
const micros = micros => make(micros * bigint1e3);

/**
 * @since 2.0.0
 * @category constructors
 */
const millis = millis => make(millis);

/**
 * @since 2.0.0
 * @category constructors
 */
const seconds = seconds => make(seconds * 1000);

/**
 * @since 2.0.0
 * @category constructors
 */
const minutes = minutes => make(minutes * 60_000);

/**
 * @since 2.0.0
 * @category constructors
 */
const hours = hours => make(hours * 3_600_000);

/**
 * @since 2.0.0
 * @category constructors
 */
const days = days => make(days * 86_400_000);

/**
 * @since 2.0.0
 * @category constructors
 */
const weeks = weeks => make(weeks * 604_800_000);

/**
 * @since 2.0.0
 * @category getters
 */
const toMillis = self => {
  const _self = decode(self);
  switch (_self.value._tag) {
    case "Infinity":
      return Infinity;
    case "Nanos":
      return Number(_self.value.nanos) / 1_000_000;
    case "Millis":
      return _self.value.millis;
  }
};

/**
 * @since 2.0.0
 * @category getters
 */
const toSeconds = self => toMillis(self) / 1_000;

/**
 * Get the duration in nanoseconds as a bigint.
 *
 * If the duration is infinite, returns `Option.none()`
 *
 * @since 2.0.0
 * @category getters
 */
const toNanos = self => {
  const _self = decode(self);
  switch (_self.value._tag) {
    case "Infinity":
      return none();
    case "Nanos":
      return some(_self.value.nanos);
    case "Millis":
      return some(BigInt(Math.round(_self.value.millis * 1_000_000)));
  }
};

/**
 * Get the duration in nanoseconds as a bigint.
 *
 * If the duration is infinite, it throws an error.
 *
 * @since 2.0.0
 * @category getters
 */
const unsafeToNanos = self => {
  const _self = decode(self);
  switch (_self.value._tag) {
    case "Infinity":
      throw new Error("Cannot convert infinite duration to nanos");
    case "Nanos":
      return _self.value.nanos;
    case "Millis":
      return BigInt(Math.round(_self.value.millis * 1_000_000));
  }
};

/**
 * @since 2.0.0
 * @category getters
 */
const toHrTime = self => {
  const _self = decode(self);
  switch (_self.value._tag) {
    case "Infinity":
      return [Infinity, 0];
    case "Nanos":
      return [Number(_self.value.nanos / bigint1e9), Number(_self.value.nanos % bigint1e9)];
    case "Millis":
      return [Math.floor(_self.value.millis / 1000), Math.round(_self.value.millis % 1000 * 1_000_000)];
  }
};

/**
 * @since 2.0.0
 * @category pattern matching
 */
const match = /*#__PURE__*/dual(2, (self, options) => {
  const _self = decode(self);
  switch (_self.value._tag) {
    case "Nanos":
      return options.onNanos(_self.value.nanos);
    case "Infinity":
      return options.onMillis(Infinity);
    case "Millis":
      return options.onMillis(_self.value.millis);
  }
});

/**
 * @since 2.0.0
 * @category pattern matching
 */
const matchWith = /*#__PURE__*/dual(3, (self, that, options) => {
  const _self = decode(self);
  const _that = decode(that);
  if (_self.value._tag === "Infinity" || _that.value._tag === "Infinity") {
    return options.onMillis(toMillis(_self), toMillis(_that));
  } else if (_self.value._tag === "Nanos" || _that.value._tag === "Nanos") {
    const selfNanos = _self.value._tag === "Nanos" ? _self.value.nanos : BigInt(Math.round(_self.value.millis * 1_000_000));
    const thatNanos = _that.value._tag === "Nanos" ? _that.value.nanos : BigInt(Math.round(_that.value.millis * 1_000_000));
    return options.onNanos(selfNanos, thatNanos);
  }
  return options.onMillis(_self.value.millis, _that.value.millis);
});

/**
 * @category instances
 * @since 2.0.0
 */
const Order = /*#__PURE__*/make$1((self, that) => matchWith(self, that, {
  onMillis: (self, that) => self < that ? -1 : self > that ? 1 : 0,
  onNanos: (self, that) => self < that ? -1 : self > that ? 1 : 0
}));

/**
 * Checks if a `Duration` is between a `minimum` and `maximum` value.
 *
 * @category predicates
 * @since 2.0.0
 */
const between = /*#__PURE__*/between$1( /*#__PURE__*/mapInput(Order, decode));

/**
 * @category instances
 * @since 2.0.0
 */
const Equivalence = (self, that) => matchWith(self, that, {
  onMillis: (self, that) => self === that,
  onNanos: (self, that) => self === that
});
const _min = /*#__PURE__*/min$1(Order);

/**
 * @since 2.0.0
 */
const min = /*#__PURE__*/dual(2, (self, that) => _min(decode(self), decode(that)));
const _max = /*#__PURE__*/max$1(Order);

/**
 * @since 2.0.0
 */
const max = /*#__PURE__*/dual(2, (self, that) => _max(decode(self), decode(that)));
const _clamp = /*#__PURE__*/clamp$1(Order);

/**
 * @since 2.0.0
 */
const clamp = /*#__PURE__*/dual(3, (self, minimum, maximum) => _clamp(decode(self), decode(minimum), decode(maximum)));

/**
 * @since 2.0.0
 * @category math
 */
const times = /*#__PURE__*/dual(2, (self, times) => match(self, {
  onMillis: millis => make(millis * times),
  onNanos: nanos => make(nanos * BigInt(times))
}));

/**
 * @since 2.0.0
 * @category math
 */
const sum = /*#__PURE__*/dual(2, (self, that) => matchWith(self, that, {
  onMillis: (self, that) => make(self + that),
  onNanos: (self, that) => make(self + that)
}));

/**
 * @since 2.0.0
 * @category predicates
 */
const lessThan = /*#__PURE__*/dual(2, (self, that) => matchWith(self, that, {
  onMillis: (self, that) => self < that,
  onNanos: (self, that) => self < that
}));

/**
 * @since 2.0.0
 * @category predicates
 */
const lessThanOrEqualTo = /*#__PURE__*/dual(2, (self, that) => matchWith(self, that, {
  onMillis: (self, that) => self <= that,
  onNanos: (self, that) => self <= that
}));

/**
 * @since 2.0.0
 * @category predicates
 */
const greaterThan = /*#__PURE__*/dual(2, (self, that) => matchWith(self, that, {
  onMillis: (self, that) => self > that,
  onNanos: (self, that) => self > that
}));

/**
 * @since 2.0.0
 * @category predicates
 */
const greaterThanOrEqualTo = /*#__PURE__*/dual(2, (self, that) => matchWith(self, that, {
  onMillis: (self, that) => self >= that,
  onNanos: (self, that) => self >= that
}));

/**
 * @since 2.0.0
 * @category predicates
 */
const equals = /*#__PURE__*/dual(2, (self, that) => Equivalence(decode(self), decode(that)));

export { Equivalence, Order, between, clamp, days, decode, equals, greaterThan, greaterThanOrEqualTo, hours, infinity, isDuration, lessThan, lessThanOrEqualTo, match, matchWith, max, micros, millis, min, minutes, nanos, seconds, sum, times, toHrTime, toMillis, toNanos, toSeconds, unsafeToNanos, weeks, zero };
